import { useCallback, useEffect, useRef } from "react";
import { startWatch, getWatchReward } from "~/api/watch";
import { devError } from "~/lib/dev-log";
import { trackVideoEvent } from "~/lib/track-events";

const REWARD_THRESHOLD_SECONDS = 3;
const COMPLETE_RATIO = 0.95;
// Poll playback position on a fixed interval while playing. Finer than the
// browser's ~250ms `timeupdate` cadence so completion is observed even on short
// clips whose last-5% window is narrow. Reliable for clips >= ~2s (5% tail >= 100ms).
const SAMPLE_INTERVAL_MS = 100;

/**
 * Tracks video watching behavior:
 * - Backend APIs: `startWatch` on play, `getWatchReward` once watched >= 3s
 * - Analytics: `video_enter`, `video_first_frame`, `video_start`, `video_reward` and
 *   `video_complete`
 *
 * The first three form the playback-latency funnel. `video_enter` is emitted when the
 * caller reports the video became the one the user is watching — call `notifyEnter()`
 * from the same visibility check that triggers autoplay (see `VideoSlide`), so on mobile
 * it marks scrolling onto the slide and on desktop it marks the fullscreen modal opening.
 *
 * Every event carries `ms_since_enter`, measured from that moment — 0 on `video_enter`
 * itself, and a fresh reading on each later event rather than a value carried over, so
 * time-to-reward and time-to-completion are readable straight off the event.
 *
 * First-frame detection only starts after `notifyEnter()`, so preloaded offscreen slides
 * never report. It prefers `requestVideoFrameCallback` (the frame actually painted,
 * `first_frame_source: "rvfc"`) and falls back to `loadeddata` where that API is missing
 * (`first_frame_source: "loadeddata"`). When playback never begins — autoplay blocked, for
 * instance — neither `video_first_frame` (on the rVFC path) nor `video_start` is emitted,
 * so the funnel shows the drop-off.
 *
 * Playback is sampled every 100ms while the video is playing (started on `play`,
 * stopped on `pause`/`ended`). Completion fires the first time a sample reaches
 * >= 95% of the duration — no loop-wrap heuristic, so seeking backwards never
 * mis-fires a completion. Feed videos `loop`, so the native `ended` event can't be
 * relied on for completion.
 *
 * `video_start` is emitted on the first sample with actual playback progress
 * (currentTime > 0), so autoplay attempts aborted by fast scrolling (which never
 * advance the clock) don't inflate the start count.
 *
 * `contentType` must reflect the app's canonical `isEntertainment()` classification
 * from the call site — it is NOT derived from `dealId` here.
 *
 * All five events carry `video_url` and the `video_format` derived from it, so latency
 * can be broken down by container format (mp4 and m3u8 load very differently).
 *
 * `enabled` gates all tracking: it is only true for surfaces where the user is
 * actively watching (the mobile full-screen feed and the click-to-open fullscreen
 * modal). Autoplaying preview surfaces (e.g. the desktop grid/carousel cards) pass
 * `false` so hover/scroll autoplay never emits watch events or hits the backend.
 *
 * Each API and each analytics event fires at most once per `feedItemId`; because
 * the guard refs only reset when `feedItemId` changes, looping never re-emits.
 */
export function useVideoWatchTracking(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  feedItemId: string | undefined,
  videoUrl: string,
  contentType: "product" | "entertainment",
  surface: string,
  enabled: boolean
) {
  const startWatchCalledRef = useRef(false);
  const startEventSentRef = useRef(false);
  const rewardDoneRef = useRef(false);
  const completeEventSentRef = useRef(false);
  const enterTimeRef = useRef<number | null>(null);
  const firstFrameSentRef = useRef(false);
  const cancelFirstFrameRef = useRef<(() => void) | null>(null);

  // Reset tracking state when feedItemId changes
  useEffect(() => {
    startWatchCalledRef.current = false;
    startEventSentRef.current = false;
    rewardDoneRef.current = false;
    completeEventSentRef.current = false;
    enterTimeRef.current = null;
    firstFrameSentRef.current = false;

    return () => {
      cancelFirstFrameRef.current?.();
      cancelFirstFrameRef.current = null;
    };
  }, [feedItemId]);

  // Elapsed time since `video_enter`; undefined when the video was never entered.
  const msSinceEnter = useCallback(
    () => (enterTimeRef.current === null ? undefined : Math.round(performance.now() - enterTimeRef.current)),
    []
  );

  const notifyEnter = useCallback(() => {
    if (!enabled || !feedItemId) return;
    // Fires at most once per feedItemId, so scrolling back and forth never re-counts.
    if (enterTimeRef.current !== null) return;

    // Bail before claiming the enter slot. The guard above is permanent, so emitting
    // video_enter without arming first-frame detection would drop this item out of the
    // funnel for good; deferring to a later call keeps the two in lockstep.
    const video = videoRef.current;
    if (!video) return;

    enterTimeRef.current = performance.now();
    // Hard-coded 0 rather than msSinceEnter(): this event IS the zero point, and reporting
    // the sub-millisecond drift from the line above as a real measurement would be noise.
    trackVideoEvent("video_enter", feedItemId, videoUrl, contentType, surface, 0);

    const emitFirstFrame = (source: "rvfc" | "loadeddata") => {
      if (firstFrameSentRef.current) return;
      firstFrameSentRef.current = true;
      cancelFirstFrameRef.current = null;
      trackVideoEvent("video_first_frame", feedItemId, videoUrl, contentType, surface, msSinceEnter(), source);
    };

    if (typeof video.requestVideoFrameCallback === "function") {
      const handle = video.requestVideoFrameCallback(() => emitFirstFrame("rvfc"));
      cancelFirstFrameRef.current = () => video.cancelVideoFrameCallback(handle);
    } else if (video.readyState >= video.HAVE_CURRENT_DATA) {
      emitFirstFrame("loadeddata");
    } else {
      const handleLoadedData = () => emitFirstFrame("loadeddata");
      video.addEventListener("loadeddata", handleLoadedData, { once: true });
      cancelFirstFrameRef.current = () => video.removeEventListener("loadeddata", handleLoadedData);
    }
  }, [videoRef, feedItemId, videoUrl, contentType, surface, enabled, msSinceEnter]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !feedItemId || !enabled) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const sample = () => {
      const current = video.currentTime;

      // video_start on first real playback progress, not on the play() call, so
      // autoplay attempts aborted by rapid scrolling don't count as views.
      if (!startEventSentRef.current && current > 0) {
        startEventSentRef.current = true;
        trackVideoEvent("video_start", feedItemId, videoUrl, contentType, surface, msSinceEnter());
      }

      // Watch reward: backend API + analytics event, both once at 3s.
      if (current >= REWARD_THRESHOLD_SECONDS && !rewardDoneRef.current) {
        rewardDoneRef.current = true;
        getWatchReward(feedItemId).catch(e => {
          devError("Failed to get watch reward:", e);
        });
        trackVideoEvent("video_reward", feedItemId, videoUrl, contentType, surface, msSinceEnter());
      }

      // Completion: first sample at/after 95% of the duration.
      if (!completeEventSentRef.current) {
        const duration = video.duration;
        if (Number.isFinite(duration) && duration > 0 && current / duration >= COMPLETE_RATIO) {
          completeEventSentRef.current = true;
          trackVideoEvent("video_complete", feedItemId, videoUrl, contentType, surface, msSinceEnter());
        }
      }
    };

    const startSampling = () => {
      if (timer === null) timer = setInterval(sample, SAMPLE_INTERVAL_MS);
    };
    const stopSampling = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const handlePlay = () => {
      if (!startWatchCalledRef.current) {
        startWatchCalledRef.current = true;
        startWatch(feedItemId).catch(e => {
          devError("Failed to start watch:", e);
        });
      }
      startSampling();
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", stopSampling);
    video.addEventListener("ended", stopSampling);

    // Autoplay may have started before these listeners attached.
    if (!video.paused) handlePlay();

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", stopSampling);
      video.removeEventListener("ended", stopSampling);
      stopSampling();
    };
  }, [videoRef, feedItemId, videoUrl, contentType, surface, enabled, msSinceEnter]);

  return { notifyEnter };
}
